import { zonedTimeToUtc } from 'date-fns-tz';
import { addDays, subDays, isBefore, isValid, isSameMonth, addMonths, differenceInCalendarDays, startOfDay } from 'date-fns';
import { addError } from './logger';
import * as DateFNS from 'date-fns';
import { formatDateForLanguage } from '@localization/formatters/dateFormatter';
import { useMemo } from 'react';
import { useFormattedDate } from '@localization/hooks/useFormattedDate';
import type { Maybe } from '@graphql/generated-base';

const DEFAULT_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const getMonthName = (date: Date, translatedMonthNames = DEFAULT_MONTH_NAMES) => {
  return translatedMonthNames[date.getMonth()];
};

export const getDayName = (date: Date, translatedDaysOfWeek = DEFAULT_DAYS_OF_WEEK) => {
  return translatedDaysOfWeek[date.getDay()];
};

export const getLocaleDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getUrlDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}${month}${day}`;
};

export const getExtractDateFields = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');
  return { year, month: parseInt(month), day: parseInt(day) };
};

export const getLocaleTimeString = (date: Date) => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const getDaysPassed = (startTime: Date, endTime: Date): number => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
  const startDay = Math.floor(startTime.getTime() / millisecondsPerDay);
  const currentDay = Math.floor(endTime.getTime() / millisecondsPerDay);
  return currentDay - startDay;
};

export const getDateInSpecificTimezone = (date: Date, timezone: string) => {
  return zonedTimeToUtc(`${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`, timezone);
};

/**
 * Validates the provided check-in/check-out date.
 * - If the date is in the past or invalid (null or undefined), it returns `null`.
 * - If the date is in the future, it returns the original date.
 *
 * @param {Date | null} [dateCheckInCheckOut] - The check-in/check-out date to validate.
 * @returns {Date | null} - The original date if it's valid (in the future), or `null` if it's invalid or in the past.
 */
export const validateCheckInCheckOut = (dateCheckInCheckOut?: Date | null) => {
  const today = new Date();
  if (!dateCheckInCheckOut || isBefore(dateCheckInCheckOut, today)) {
    return null;
  }
  return dateCheckInCheckOut;
};

const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Creates a Date object from an unformatted date string.
 *
 * @param {string} [dateString] - The unformatted date string to convert into a Date object.
 * @param {'checkin' | 'checkout'} [type] - Specifies whether the date is for check-in or check-out.
 *   - If `checkin` is specified and the date string is invalid, the function returns a check-in date that is 1 month ahead of today, minus 1 day.
 *   - If `checkout` is specified and the date string is invalid, the function returns a check-out date that is 1 month ahead of today, plus 1 day.
 *   - If neither `checkin` nor `checkout` is specified, the function returns the current date as a fallback.
 * @returns {Date} - The converted Date object.
 */
export const createDateFromUnformattedString = (dateString?: string | null): Date | null => {
  if ((dateString && dateOnlyRegex.test(dateString)) || isValid(new Date(`${dateString}T00:00:00`))) {
    return new Date(`${dateString}T00:00:00`);
  }

  addError(`createDateFromUnformattedString: Invalid Date String - ${dateString}`);

  return null;
};

/**
 * Parses a date string that may be either date-only (YYYY-MM-DD) or a full ISO
 * timestamp (e.g. 2026-03-30T19:38:14.189Z).
 *
 * - Date-only strings are parsed as local midnight via `T00:00:00` suffix to
 *   avoid the UTC→local day-shift bug.
 * - Timestamp strings are parsed with `new Date()` which preserves the original
 *   instant (including time-of-day).
 */
export const parseDateOrTimestamp = (dateString?: string | null): Date | null => {
  if (!dateString) return null;

  if (dateOnlyRegex.test(dateString)) {
    return new Date(`${dateString}T00:00:00`);
  }

  const parsed = new Date(dateString);
  if (isValid(parsed)) return parsed;

  addError(`parseDateOrTimestamp: Invalid Date String - ${dateString}`);
  return null;
};

/**
 * Calculates the check-in and check-out dates based on the provided date string.
 * - If the date string is invalid or in the past, it calculates the dates based on the current date:
 *   - Check-in: 1 month ahead and 1 day before.
 *   - Check-out: 1 month ahead and 1 day after.
 * - If the date string is valid and in the future, it calculates the dates relative to the given date:
 *   - Check-in: 1 day before the given date.
 *   - Check-out: 1 day after the given date.
 *
 * @param {string | null} [dateString] - The date string to calculate the stay dates from.
 * @returns {Object} - An object containing the `checkIn` and `checkOut` Date objects.
 */
export const calculateStayDates = (dateString?: string | null) => {
  const currentDate = new Date();
  let checkIn: Date, checkOut: Date;
  let isDefaultDate = false;

  if (!dateString || isBefore(new Date(dateString), currentDate)) {
    // If dateString is invalid or in the past, calculate based on current date
    checkIn = subDays(addMonths(currentDate, 1), 1); // Check-in: 1 month ahead, 1 day before
    checkOut = addDays(addMonths(currentDate, 1), 1); // Check-out: 1 month ahead, 1 day after
    isDefaultDate = true;
  } else {
    // If dateString is valid and in the future, calculate relative to the given date
    checkIn = subDays(new Date(dateString), 1); // Check-in: 1 day before the given date
    checkOut = addDays(new Date(dateString), 1); // Check-out: 1 day after the given date
  }

  return {
    checkIn: {
      date: checkIn,
      dateString: getLocaleDateString(checkIn),
      extractedDateFields: getExtractDateFields(checkIn),
      urlDateString: getUrlDateString(checkIn),
    },
    checkOut: {
      date: checkOut,
      dateString: getLocaleDateString(checkOut),
      extractedDateFields: getExtractDateFields(checkOut),
      urlDateString: getUrlDateString(checkOut),
    },
    isDefaultDate,
  };
};

/**
 * Validates and returns stay dates, prioritizing provided dates.
 * Falls back to default dates (1 month ahead ±1 day) if dates are missing or in the past.
 *
 * Validation logic:
 * - Check-in: Uses provided date if valid and not before today (calendar day), otherwise defaults to 1 month ahead - 1 day
 * - Check-out: Uses provided date if valid, not before today, and after check-in, otherwise defaults to check-in + 2 days
 * - Dates are normalized to start-of-day to avoid timezone/time-of-day issues
 *
 * @param {string | null} [checkInString] - The check-in date string to validate.
 * @param {string | null} [checkOutString] - The check-out date string to validate.
 * @returns {Object} - An object containing validated `checkInDate` and `checkOutDate` Date objects.
 */
export const getValidatedStayDates = (checkInString?: string | null, checkOutString?: string | null) => {
  // Normalize current date to start-of-day for calendar day comparison
  const today = startOfDay(new Date());
  let checkInDate: Date;
  let checkOutDate: Date;

  // Validate check-in date
  if (checkInString) {
    const parsedCheckIn = startOfDay(new Date(checkInString));
    // Allow today and future dates
    if (!isBefore(parsedCheckIn, today)) {
      checkInDate = parsedCheckIn;
    } else {
      // Check-in is in the past, use default (30 days from today)
      checkInDate = addDays(today, 30);
    }
  } else {
    // No check-in provided, use default (30 days from today)
    checkInDate = addDays(today, 30);
  }

  // Validate check-out date
  if (checkOutString) {
    const parsedCheckOut = startOfDay(new Date(checkOutString));
    // Check-out must be after check-in (check-in is already >= today)
    if (!isBefore(parsedCheckOut, checkInDate)) {
      checkOutDate = parsedCheckOut;
    } else {
      // Check-out is before check-in, use default (check-in + 2 days)
      checkOutDate = addDays(checkInDate, 2);
    }
  } else {
    // No check-out provided, default to check-in + 2 days
    checkOutDate = addDays(checkInDate, 2);
  }

  return { checkInDate, checkOutDate };
};

/**
 * Determines check in and check out have same month.
 *
 * @param {string} [checkIn] - The check in date string to check.
 * @param {string} [checkOut] - The checkout date string to check.
 * @returns {boolean} - The Check in and Check out dates have same month.
 */
export const isSameMonthCheckInCheckOut = (checkIn: string, checkOut: string) => {
  if (isSameMonth(new Date(checkIn), new Date(checkOut))) {
    return true;
  }
  return false;
};

/**
 * Determine if given Date is 90 or fewer days away.
 *
 * @param {string} [dateStringCutOffDate] - The date object to transform in date string.
 * @returns {boolean} - The date is 90 days or fewer days away.
 */
export const isCutOffDateNinetyOrFewerDaysAway = (dateStringCutOffDate: string) => {
  const today = new Date();
  if (differenceInCalendarDays(new Date(dateStringCutOffDate), today) <= 90) {
    return true;
  }
  return false;
};

/**
 * Determines if an event date is more than N days away from now.
 * Optimized to use Date.now() for better performance.
 *
 * @param {Date | null} eventDate - The event date to check.
 * @param {number} numberOfDays - The number of days to compare against.
 * @returns {boolean} - True if the event is more than N days away, false otherwise.
 */
export const isEventMoreThanNDaysAway = (eventDate: Date | null, numberOfDays: number): boolean => {
  if (!eventDate) return false;

  const now = Date.now();
  const daysDifference = Math.ceil((eventDate.getTime() - now) / (1000 * 60 * 60 * 24));

  return daysDifference > numberOfDays;
};

/**
 * Formats a given date to 'yyyy-MM-dd' format for travel-related operations.
 *
 * @param date - The Date object to format. Can be undefined or null.
 * @returns A string in 'yyyy-MM-dd' format if the date is valid, otherwise an empty string.
 */
export const formatDateForTravel = (date?: Date | null): string => {
  return date ? DateFNS.format(date, 'yyyy-MM-dd') : '';
};

/**
 * Formats a single date in the format "MMM d" Ex. "Feb 26".
 *
 * @param dateString - The date string to format.
 * @param language - BCP 47 language tag (defaults to 'en-US').
 * @returns A formatted string like "Feb 26".
 */
export const getDateWithMonthAndDay = (dateString: string, language: string = 'en-US') => {
  const date = new Date(dateString);

  // PR #11978 Comment #12: Changed 'MMMd' → 'MMMdd' (removed duplicate format)
  return formatDateForLanguage(language, date, 'MMMdd') ?? '';
};

/**
 * Gets different parts of the date including month, day, year and time
 * for use with string interpolation in the "Booked on [Month] [Day]th, [Year] at [Time]" format
 *
 * @param dateString - The date string to format
 * @param language - BCP 47 language tag (defaults to 'en-US').
 * @returns different parts of the date including month, day, year and time
 */
export const getDateFieldsWithTime = (dateString: string, language: string = 'en-US') => {
  const date = new Date(dateString);

  const month = formatDateForLanguage(language, date, 'MMMM') ?? ''; // Full month name (July)
  const day = formatDateForLanguage(language, date, 'dd') ?? ''; // Day with leading zero (09)
  const year = formatDateForLanguage(language, date, 'yyyy') ?? '';
  const time = formatDateForLanguage(language, date, 'hh:mm') ?? ''; // 12-hour format with leading zero (09:30 AM)

  return { month, day, year, time };
};

/**
 * Hook for getting different parts of the date including month, day, year and time
 * for use with string interpolation in the "Booked on [Month] [Day]th, [Year] at [Time]" format.
 * Uses the current locale from context.
 *
 * @param dateString - The date string to format
 * @returns different parts of the date including month, day, year and time
 */
export const useGetDateFieldsWithTime = (dateString: string | null | undefined): Readonly<{ month: string; day: string; year: string; time: string }> => {
  const date = useMemo(() => (dateString ? new Date(dateString) : null), [dateString]);
  const month = useFormattedDate(date, 'MMMM') ?? '';
  const day = useFormattedDate(date, 'dd') ?? '';
  const year = useFormattedDate(date, 'yyyy') ?? '';
  const time = useFormattedDate(date, 'hh:mm') ?? '';

  return useMemo(() => ({ month, day, year, time }), [month, day, year, time]);
};

/**
 * Formats date components into a string in the format 'yyyy-MM-dd'.
 *
 * ex. { year: 2025, month: 7, day: 15 } -> '2025-07-15'
 * @param date - An object containing year, month, and day.
 * @return A string representing the date in 'yyyy-MM-dd' format.
 */
export const formatDateComponentsToString = (date: { year: number; month: number; day: number }) => {
  return DateFNS.format(
    new Date(
      date.year,
      // must decrement month by 1 since Date constructor expects 0-indexed month
      date.month - 1,
      date.day,
    ),
    'yyyy-MM-dd',
  );
};

/**
 * Formats date components into a string in the format 'yyyy-MM-dd' for URL usage.
 * This is a utility function that formats date parts consistently.
 *
 * @param date - An object containing year, month, and day.
 * @return A string representing the date in 'yyyy-MM-dd' format.
 */
export const formatDateForUrl = (date: { year: number; month: number; day: number }): string => {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
};

/**
 * Converts date parts to a Date object using the shared date helper.
 *
 * @param date - An object containing year, month, and day.
 * @returns A Date object or null if the date is invalid.
 */
export const convertToDateFromDateParts = (date: { year: number; month: number; day: number }): Date | null => {
  return createDateFromUnformattedString(formatDateForUrl(date));
};

/**
 * Converts date parts to a date range with start and end dates.
 * If no dates are provided, defaults to today and 3 days from now.
 *
 * @param startDateInput - Optional start date parts.
 * @param endDateInput - Optional end date parts.
 * @returns An object containing startDate and endDate.
 */
export const convertToDateRangeFromDateParts = (
  startDateInput?: { year: number; month: number; day: number },
  endDateInput?: { year: number; month: number; day: number },
): { startDate: Date; endDate: Date } => {
  const today = new Date();
  const startDate = startDateInput ? (convertToDateFromDateParts(startDateInput) ?? today) : today;

  const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
  const endDate = endDateInput ? (convertToDateFromDateParts(endDateInput) ?? threeDaysFromNow) : threeDaysFromNow;
  return { startDate, endDate };
};

/**
 * hasEnoughTimeElapsedSinceDate - Checks if the time elapsed is greater than the duration hours.
 * @param durationHours - The duration hours.
 * @param fromDate - The date from which the time elapsed is calculated.
 * @returns True if the time elapsed is greater than the duration hours, false otherwise.
 */
export const hasEnoughTimeElapsedSinceDate = (durationHours: number, fromDate?: Maybe<Date>): boolean => {
  if (!fromDate) return false;

  const now = new Date();
  const msElapsed = now.getTime() - fromDate.getTime();
  const durationMS = durationHours * 60 * 60 * 1000;
  return msElapsed >= durationMS;
};
